import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { Settings, ChevronLeft, MessageSquare, X, Type, List, Bookmark as BookmarkIcon, Lock } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Turnstile } from '../components/ui/Turnstile';
import { getBook, getChapter, getComments, postComment, getBookmarks, addBookmark, deleteBookmark, saveProgress } from '../services/api';

export const Reader: React.FC = () => {
  const { bookId, chapterId } = useParams<{ bookId: string; chapterId: string }>();
  const navigate = useNavigate();
  const { theme, setTheme, bgClass, surfaceClass } = useTheme();
  const { user, requestMagic } = useAuth();
  
  const [fontSize, setFontSize] = useState(18);
  const [lineHeight, setLineHeight] = useState(1.8);
  const [showSettings, setShowSettings] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [showTOC, setShowTOC] = useState(false);
  const [newComment, setNewComment] = useState('');
  
  const [book, setBook] = useState<any>(null);
  const [chapter, setChapter] = useState<any>(null);
  const [comments, setComments] = useState<any[]>([]);
  const [bookmarks, setBookmarks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showAuth, setShowAuth] = useState(false);
  const [email, setEmail] = useState('');
  const [authStep, setAuthStep] = useState<'input' | 'sent'>('input');
  const [captchaToken, setCaptchaToken] = useState('');
  const [isSendingLink, setIsSendingLink] = useState(false);

  // Derived chapter navigation info (safe defaults)
  const chapterIndex = book?.chapters?.findIndex((c: any) => c.id === chapterId) ?? 0;
  const prevChapter = book?.chapters?.[chapterIndex - 1];
  const nextChapter = book?.chapters?.[chapterIndex + 1];
  const isBookmarked = !!bookmarks.find(b => b.book_id === bookId && b.chapter_id === chapterId);

  useEffect(() => {
    const load = async () => {
      if (!bookId || !chapterId) {
        setError('Missing book or chapter');
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const bookData = await getBook(bookId);
        const chapterData = await getChapter(bookId, chapterId);

        setBook(bookData);
        setChapter(chapterData);
        const commentsRes = await getComments(bookId, chapterId);
        setComments(commentsRes.comments || []);
        if (user) {
          const bm = await getBookmarks();
          setBookmarks(bm.bookmarks || []);
        }
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [bookId, chapterId, user]);

  useEffect(() => {
    if (chapter && book && user && !chapter.locked) {
      const pct = Math.max(
        1,
        Math.min(100, Math.round(((chapterIndex + 1) / (book.chapters?.length || 1)) * 100))
      );
      saveProgress({ bookId: bookId!, chapterId: chapterId!, percentage: pct, minutes: 1 }).catch(() => {});
    }
  }, [chapter, book, user, chapterIndex, bookId, chapterId]);

  const toggleBookmark = async () => {
    if (!user) {
      setShowAuth(true);
      return;
    }
    try {
      if (isBookmarked) {
        const bm = bookmarks.find(b => b.book_id === bookId && b.chapter_id === chapterId);
        if (bm) {
          await deleteBookmark(bm.id);
          setBookmarks(prev => prev.filter(p => p.id !== bm.id));
        }
      } else {
        await addBookmark({ bookId: bookId!, chapterId: chapterId! });
        const refreshed = await getBookmarks();
        setBookmarks(refreshed.bookmarks || []);
      }
    } catch (err) {
      alert((err as Error).message);
    }
  };

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!captchaToken) {
      alert('Please complete the verification.');
      return;
    }
    setIsSendingLink(true);
    try {
      await requestMagic(email, captchaToken);
      setAuthStep('sent');
      setTimeout(() => {
        setShowAuth(false);
      }, 2000);
    } catch (err) {
      alert((err as Error).message || 'Failed to send magic link.');
      setCaptchaToken('');
    } finally {
      setIsSendingLink(false);
    }
  };

  const handlePostComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      setShowAuth(true);
      return;
    }
    if (!newComment.trim()) return;
    try {
      await postComment(bookId!, chapterId!, newComment.trim());
      const updated = await getComments(bookId!, chapterId!);
      setComments(updated.comments || []);
      setNewComment('');
    } catch (err) {
      alert((err as Error).message);
    }
  };

  if (loading) return <div className={`min-h-screen flex items-center justify-center ${bgClass}`}>Loading...</div>;

  if (error || !book || !chapter) {
    return (
      <div className="p-8 text-center space-y-4">
        <p className="text-red-500">{error || 'Chapter not found'}</p>
        <Button onClick={() => navigate(-1)} variant="outline">
          Go Back
        </Button>
      </div>
    );
  }

  const isLocked = !!chapter.locked;
  const chapterContent = isLocked ? chapter.preview || '' : chapter.content || chapter.content_html;

  return (
    <div className={`min-h-screen pb-32 transition-colors duration-300`}>
      {/* Reader Header / Toolbar */}
      <div className={`fixed top-0 left-0 right-0 z-30 h-14 flex items-center justify-between px-4 backdrop-blur-md bg-opacity-95 border-b border-black/5 dark:border-white/5 transition-transform duration-300 ${bgClass} ${showSettings ? 'translate-y-0' : '-translate-y-full md:translate-y-0'}`}>
        <div className="flex items-center gap-1">
            <Link to={`/book/${bookId}`} className="p-2 -ml-2 opacity-60 hover:opacity-100 rounded-full hover:bg-black/5 dark:hover:bg-white/5 transition-colors">
            <ChevronLeft size={24} />
            </Link>
            <button onClick={() => setShowTOC(true)} className="p-2 opacity-60 hover:opacity-100 rounded-full hover:bg-black/5 dark:hover:bg-white/5 transition-colors">
                <List size={20} />
            </button>
        </div>

        <span className="text-xs font-medium uppercase tracking-widest opacity-50 truncate max-w-[150px] hidden sm:block">
          {book.title}
        </span>
        
        <div className="flex gap-1 items-center">
          <button 
            onClick={toggleBookmark} 
            className={`p-2 rounded-full hover:bg-black/5 dark:hover:bg-white/5 transition-colors ${isBookmarked ? 'text-brand-blue opacity-100' : 'opacity-60 hover:opacity-100'}`}
          >
            <BookmarkIcon size={20} fill={isBookmarked ? "currentColor" : "none"} />
          </button>
          <button onClick={() => setShowSettings(!showSettings)} className="p-2 opacity-60 hover:opacity-100 rounded-full hover:bg-black/5 dark:hover:bg-white/5 transition-colors">
            <Settings size={20} />
          </button>
        </div>
      </div>

      {/* Touch Area for Menu Toggle (Mobile) */}
      <div onClick={() => setShowSettings(!showSettings)} className="fixed top-0 left-0 right-0 h-14 z-20 md:hidden" />

      {/* Main Text Content */}
      <main className="max-w-2xl mx-auto px-6 pt-24">
        <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-widest opacity-40 mb-2">Chapter {chapterIndex + 1}</p>
            <h1 className="font-serif text-3xl md:text-4xl font-bold mb-4">{chapter.title}</h1>
            <div className="text-xs font-medium opacity-50 flex items-center justify-center gap-2">
                <span>{book.author}</span>
                <span>•</span>
                <span>{Math.ceil((chapter.word_count || 0) / 200)} min read</span>
            </div>
        </div>

        <div 
          className="font-serif transition-all duration-300 ease-in-out prose dark:prose-invert max-w-none"
          style={{ 
            fontSize: `${fontSize}px`, 
            lineHeight: lineHeight 
          }}
        >
          {isLocked ? (
             <>
               {chapter.preview ? <div dangerouslySetInnerHTML={{ __html: chapter.preview }} /> : null}
               <div className="relative mt-4">
                 <div className="blur-sm select-none opacity-40 pointer-events-none">
                  <div dangerouslySetInnerHTML={{ __html: chapter.preview || '' }} />
                 </div>
                 
                 <div className={`absolute inset-0 -top-12 bg-gradient-to-b from-transparent via-paper-bg/90 to-paper-bg dark:via-night-bg/90 dark:to-night-bg ${theme === 'sepia' ? 'via-sepia-bg/90 to-sepia-bg' : ''} flex flex-col items-center justify-end pb-12`}>
                    <div className={`p-8 rounded-2xl shadow-2xl max-w-sm mx-auto text-center border m-4 ${surfaceClass}`}>
                      <Lock size={32} className="mx-auto mb-4 text-brand-blue opacity-80" />
                      <h3 className="text-xl font-bold mb-2 font-sans">Continue Reading</h3>
                      <p className="text-sm opacity-70 mb-6 leading-relaxed">
                        You've reached the free preview limit for this book. Enter your email to unlock the full story instantly.
                      </p>
                      <Button onClick={() => setShowAuth(true)} fullWidth size="lg" className="shadow-brand-blue/30 shadow-lg">
                        Unlock with Magic Link
                      </Button>
                      <p className="text-[10px] mt-4 opacity-40">No password required.</p>
                    </div>
                 </div>
               </div>
             </>
          ) : (
            <div dangerouslySetInnerHTML={{ __html: chapterContent || '' }} />
          )}
        </div>

        {/* Chapter Navigation */}
        {!isLocked && (
            <div className="mt-20 pt-8 border-t border-black/10 dark:border-white/10 flex justify-between items-center">
                {prevChapter ? (
                    <Link to={`/book/${bookId}/read/${prevChapter.id}`} className="group pl-2 pr-4 py-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 flex items-center gap-2 transition-all">
                        <ChevronLeft size={20} className="opacity-50 group-hover:-translate-x-1 transition-transform" />
                        <div className="text-left">
                            <span className="block text-xs opacity-50 uppercase tracking-wider">Previous</span>
                            <span className="font-serif font-bold text-sm">Chapter {chapterIndex}</span>
                        </div>
                    </Link>
                ) : <div/>}
                
                {nextChapter ? (
                    <Link to={`/book/${bookId}/read/${nextChapter.id}`} className="group pl-6 pr-4 py-3 bg-brand-blue text-white rounded-full flex items-center gap-2 shadow-lg hover:bg-brand-hover hover:shadow-xl hover:-translate-y-0.5 transition-all">
                        <span className="font-medium text-sm">Next Chapter</span>
                        <ChevronLeft size={16} className="rotate-180" />
                    </Link>
                ) : (
                    <span className="text-sm opacity-50 italic">End of Book</span>
                )}
            </div>
        )}

        {/* Comments Section */}
        <div className="mt-16 text-center pb-8">
           <div className="relative">
             <div className="absolute inset-0 flex items-center" aria-hidden="true">
               <div className="w-full border-t border-black/10 dark:border-white/10"></div>
             </div>
             <div className="relative flex justify-center">
               <span className={`px-3 text-sm opacity-50 font-serif italic ${bgClass}`}>Discussion</span>
             </div>
           </div>
           
           <div className="mt-8">
               <button onClick={() => setShowComments(true)} className={`inline-flex items-center gap-2 text-sm font-medium py-3 px-6 rounded-full border border-black/10 dark:border-white/10 hover:border-brand-blue hover:text-brand-blue transition-all shadow-sm ${surfaceClass}`}>
                  <MessageSquare size={18} /> 
                  Show {comments.length} Comments
               </button>
           </div>
        </div>
      </main>

      {/* Settings Bottom Sheet */}
      <div className={`fixed bottom-0 left-0 right-0 z-40 border-t p-6 rounded-t-3xl shadow-[0_-10px_40px_rgba(0,0,0,0.1)] transition-transform duration-300 ease-out ${surfaceClass} ${showSettings ? 'translate-y-0' : 'translate-y-full'}`}>
        <div className="max-w-md mx-auto space-y-8">
           <div className="flex justify-between items-center">
             <h3 className="font-bold text-lg">Appearance</h3>
             <button onClick={() => setShowSettings(false)} className="p-2 bg-black/5 dark:bg-white/10 rounded-full hover:bg-black/10"><X size={18}/></button>
           </div>

           {/* Theme Selector */}
           <div className="flex gap-3">
              {(['paper', 'sepia', 'night'] as const).map(t => (
                  <button 
                    key={t}
                    onClick={() => setTheme(t)}
                    className={`flex-1 py-4 rounded-xl border-2 flex flex-col items-center gap-2 transition-all ${theme === t ? 'border-brand-blue bg-brand-blue/5' : 'border-transparent bg-black/5 dark:bg-white/5 opacity-70 hover:opacity-100'}`}
                  >
                    <div className={`w-6 h-6 rounded-full border shadow-sm ${t === 'paper' ? 'bg-[#f7f3e9]' : t === 'sepia' ? 'bg-[#f3ecd2]' : 'bg-[#1a1a1a] border-gray-600'}`} />
                    <span className="text-xs font-bold uppercase tracking-wider">{t}</span>
                  </button>
              ))}
           </div>

           {/* Typography Controls */}
           <div className="space-y-6">
               <div className="flex items-center gap-4">
                  <span className="text-xs font-bold opacity-50 w-16 uppercase tracking-wider">Size</span>
                  <span className="p-2"><Type size={14} /></span>
                  <input 
                    type="range" 
                    min="14" 
                    max="24" 
                    step="1"
                    value={fontSize} 
                    onChange={(e) => setFontSize(Number(e.target.value))} 
                    className="flex-1 h-2 bg-black/10 dark:bg-white/10 rounded-lg appearance-none cursor-pointer accent-brand-blue"
                  />
                  <span className="p-2"><Type size={22} /></span>
               </div>

               <div className="flex items-center gap-4">
                  <span className="text-xs font-bold opacity-50 w-16 uppercase tracking-wider">Spacing</span>
                  <span className="text-xs font-serif leading-none px-1 py-2 border rounded opacity-60">Aa</span>
                  <input 
                    type="range" 
                    min="1.4" 
                    max="2.2" 
                    step="0.1"
                    value={lineHeight} 
                    onChange={(e) => setLineHeight(Number(e.target.value))} 
                    className="flex-1 h-2 bg-black/10 dark:bg-white/10 rounded-lg appearance-none cursor-pointer accent-brand-blue"
                  />
                   <span className="text-xs font-serif leading-loose px-1 py-2 border rounded opacity-60">Aa</span>
               </div>
           </div>
        </div>
      </div>

      {/* Table of Contents Side Sheet */}
      {showTOC && (
         <>
            <div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" onClick={() => setShowTOC(false)} />
            <div className={`fixed inset-y-0 left-0 z-50 w-80 max-w-[80%] shadow-2xl p-6 overflow-y-auto animate-in slide-in-from-left duration-300 ${surfaceClass}`}>
                <div className="flex justify-between items-center mb-6">
                    <h2 className="font-bold text-lg">Chapters</h2>
                    <button onClick={() => setShowTOC(false)}><X size={20}/></button>
                </div>
                <div className="space-y-1">
                    {book?.chapters?.map((c: any, i: number) => (
                        <Link 
                           key={c.id} 
                           to={`/book/${bookId}/read/${c.id}`}
                           onClick={() => setShowTOC(false)}
                           className={`block p-3 rounded-lg text-sm ${c.id === chapterId ? 'bg-brand-blue text-white font-bold' : 'hover:bg-black/5 dark:hover:bg-white/5 opacity-80'}`}
                        >
                            <span className="opacity-50 mr-2">{i + 1}.</span> {c.title}
                            {!c.is_free && !user && <Lock size={12} className="inline ml-2 opacity-50" />}
                        </Link>
                    ))}
                </div>
            </div>
         </>
      )}

      {/* Comments Side Sheet */}
      {showComments && (
        <>
          <div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" onClick={() => setShowComments(false)} />
          <div className={`fixed inset-y-0 right-0 z-50 w-full md:w-96 shadow-2xl p-0 overflow-hidden animate-in slide-in-from-right duration-300 flex flex-col ${bgClass}`}>
            <div className={`flex items-center justify-between p-4 border-b border-black/5 dark:border-white/5 ${bgClass}`}>
              <h2 className="font-bold text-lg">Comments ({comments.length})</h2>
              <button onClick={() => setShowComments(false)} className="p-2 hover:bg-black/5 rounded-full"><X size={20} /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4 no-scrollbar">
               {comments.map(c => (
                 <div key={c.id} className={`p-4 rounded-xl border shadow-sm ${surfaceClass}`}>
                    <div className="flex justify-between items-baseline mb-2">
                       <span className="font-bold text-sm text-brand-blue">{c.email || 'Reader'}</span>
                       <span className="text-[10px] uppercase tracking-wide opacity-40">{c.created_at}</span>
                    </div>
                    <p className="text-sm opacity-80 leading-relaxed font-serif">{c.content}</p>
                 </div>
               ))}
               {comments.length === 0 && <div className="text-center opacity-50 py-10 italic">Be the first to share your thoughts.</div>}
            </div>

            {/* Comment Input */}
            <div className={`p-4 border-t border-black/10 dark:border-white/10 ${bgClass}`}>
              {user ? (
                <form onSubmit={handlePostComment} className="flex flex-col gap-3">
                  <textarea 
                    className={`w-full rounded-xl p-3 text-sm focus:ring-2 focus:ring-brand-blue outline-none resize-none transition-all ${surfaceClass}`}
                    rows={2}
                    placeholder="Add to the discussion..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                  />
                  <div className="flex justify-end">
                    <Button size="sm" type="submit" disabled={!newComment.trim()}>Post</Button>
                  </div>
                </form>
              ) : (
                <div className={`text-center p-5 rounded-xl border border-dashed border-black/10 dark:border-white/10 ${surfaceClass}`}>
                  <p className="text-sm opacity-70 mb-3 font-medium">Join the conversation</p>
                  <Button size="sm" variant="outline" onClick={() => { setShowComments(false); setShowAuth(true); }} fullWidth className={bgClass}>
                    Sign In to Comment
                  </Button>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Auth Modal */}
      {showAuth && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className={`w-full max-w-sm rounded-2xl p-8 shadow-2xl transform scale-100 animate-in zoom-in-95 duration-200 ${surfaceClass}`}>
             {authStep === 'input' ? (
                 <form onSubmit={handleMagicLink}>
                    <div className="w-12 h-12 bg-brand-blue/10 rounded-full flex items-center justify-center text-brand-blue mb-4 mx-auto">
                        <Lock size={24} />
                    </div>
                    <h2 className="text-2xl font-serif font-bold mb-2 text-center">Unlock Full Access</h2>
                    <p className="text-sm opacity-70 mb-6 text-center leading-relaxed">
                        Enter your email to continue reading. We'll send a magic link to verify it's you.
                    </p>
                    <div className="space-y-3">
                        <input 
                          type="email" 
                          required
                          placeholder="name@example.com" 
                          className="w-full px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent focus:ring-2 focus:ring-brand-blue outline-none transition-all"
                          value={email}
                          onChange={e => setEmail(e.target.value)}
                        />
                        <Turnstile 
                          siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY || ''} 
                          onVerify={(token) => setCaptchaToken(token)} 
                          onError={() => setCaptchaToken('')}
                        />
                        <Button type="submit" fullWidth size="lg" disabled={isSendingLink || !captchaToken}>
                          {isSendingLink ? 'Sending...' : 'Send Magic Link'}
                        </Button>
                        <button type="button" onClick={() => setShowAuth(false)} className="w-full py-2 text-xs font-medium opacity-50 hover:opacity-100">Cancel</button>
                    </div>
                 </form>
             ) : (
                 <div className="text-center py-4">
                    <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl animate-in zoom-in spin-in-3">✓</div>
                    <h3 className="text-xl font-bold mb-2">Check your email</h3>
                    <p className="opacity-70 text-sm mb-6">We sent a secure link to<br/><strong>{email}</strong></p>
                    <div className="h-1 w-full bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-green-500 w-1/2 animate-pulse"></div>
                    </div>
                    <p className="text-[10px] opacity-40 mt-4 uppercase tracking-widest">Verifying...</p>
                 </div>
             )}
          </div>
        </div>
      )}
    </div>
  );
};
